import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { FadeIn } from "./FadeIn";
import {
  GALLERY_WEDDING_ONE_URL,
  GALLERY_WEDDING_TWO_URL,
  GALLERY_WEDDING_THREE_URL,
  GALLERY_WEDDING_FOUR_URL,
  GALLERY_CORPORATE_ONE_URL,
  GALLERY_CORPORATE_TWO_URL,
  GALLERY_CORPORATE_THREE_URL,
  GALLERY_PREWEDDING_ONE_URL,
  GALLERY_PREWEDDING_TWO_URL,
  GALLERY_PREWEDDING_THREE_URL,
  GALLERY_NIGHT_ONE_URL,
  GALLERY_NIGHT_TWO_URL,
  GALLERY_NIGHT_THREE_URL,
  GALLERY_NIGHT_FOUR_URL,
} from "@/config/assets";

const CATEGORIES = ["All", "Royal Weddings", "Corporate Evenings", "Pre-Wedding Functions", "Night Illuminations"];

const PHOTOS = [
  { url: GALLERY_WEDDING_ONE_URL, cat: "Royal Weddings", alt: "Grand pink floral arches with chandeliers" },
  { url: GALLERY_NIGHT_ONE_URL, cat: "Night Illuminations", alt: "Golden mandap glowing at twilight" },
  { url: GALLERY_CORPORATE_ONE_URL, cat: "Corporate Evenings", alt: "Conference seating rows under elegant canopy" },
  { url: GALLERY_PREWEDDING_ONE_URL, cat: "Pre-Wedding Functions", alt: "Romantic garden arch seating with lush florals" },
  { url: GALLERY_WEDDING_TWO_URL, cat: "Royal Weddings", alt: "Elegant white floral wedding stage" },
  { url: GALLERY_NIGHT_TWO_URL, cat: "Night Illuminations", alt: "Illuminated lotus stage with mirror reflection" },
  { url: GALLERY_CORPORATE_TWO_URL, cat: "Corporate Evenings", alt: "Corporate evening lounge arrangement" },
  { url: GALLERY_PREWEDDING_TWO_URL, cat: "Pre-Wedding Functions", alt: "Traditional haldi ceremony decor" },
  { url: GALLERY_WEDDING_THREE_URL, cat: "Royal Weddings", alt: "White paneled stage with floral borders" },
  { url: GALLERY_NIGHT_THREE_URL, cat: "Night Illuminations", alt: "Glowing entrance aisle at night" },
  { url: GALLERY_CORPORATE_THREE_URL, cat: "Corporate Evenings", alt: "Grand gala tent with stage setup" },
  { url: GALLERY_PREWEDDING_THREE_URL, cat: "Pre-Wedding Functions", alt: "Floral photo backdrop amidst palms" },
  { url: GALLERY_WEDDING_FOUR_URL, cat: "Royal Weddings", alt: "Outdoor wedding ceremony stage with florals" },
  { url: GALLERY_NIGHT_FOUR_URL, cat: "Night Illuminations", alt: "Golden mandap on the lawn at dusk" },
].filter((p) => p.url);

export const Gallery = () => {
  const [active, setActive] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const visible = active === "All" ? PHOTOS : PHOTOS.filter((p) => p.cat === active);

  return (
    <section id="gallery" data-testid="gallery-section" className="grain-overlay bg-white py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <FadeIn>
          <p className="text-[#097969] uppercase tracking-[0.4em] text-xs mb-5">Visual Grandeur</p>
          <h2 className="font-serif-luxe text-4xl md:text-5xl font-medium text-[#043927] leading-tight">
            The <span className="italic">Gallery</span>
          </h2>
        </FadeIn>

        <FadeIn delay={0.1}>
          <div className="mt-12 flex flex-wrap gap-3">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`gallery-tab-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => setActive(c)}
                className={`px-6 py-2.5 rounded-full text-sm tracking-wide border transition-colors duration-300 ${
                  active === c
                    ? "bg-[#043927] text-[#F3E5AB] border-[#043927]"
                    : "bg-transparent text-[#043927] border-[#097969]/30 hover:border-[#D4AF37]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </FadeIn>

        {visible.length === 0 && (
          <p data-testid="gallery-empty-state" className="mt-14 font-serif-luxe italic text-2xl text-[#097969]">
            Photographs for this collection are coming soon.
          </p>
        )}

        <div className="mt-14 gallery-masonry">
          <AnimatePresence>
            {visible.map((p) => (
              <motion.div
                key={p.url}
                layout
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.5 }}
                className="mb-6 break-inside-avoid"
              >
                <button
                  data-testid={`gallery-item-${p.cat.toLowerCase().replace(/[^a-z]+/g, "-")}-${PHOTOS.indexOf(p)}`}
                  onClick={() => setLightbox(p)}
                  className="group relative block w-full rounded-sm overflow-hidden border border-[#D4AF37]/20"
                >
                  <img src={p.url} alt={p.alt} loading="lazy" className="w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-[#043927]/0 group-hover:bg-[#043927]/40 transition-colors duration-500 flex items-end">
                    <p className="p-5 text-[#F3E5AB] text-sm tracking-[0.2em] uppercase opacity-0 group-hover:opacity-100 transition-opacity duration-500">{p.cat}</p>
                  </div>
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center p-6"
            onClick={() => setLightbox(null)}
          >
            <button data-testid="gallery-lightbox-close" className="absolute top-6 right-6 text-[#D4AF37] hover:text-[#F3E5AB] transition-colors duration-300" aria-label="Close">
              <X size={32} />
            </button>
            <motion.img
              initial={{ scale: 0.92 }}
              animate={{ scale: 1 }}
              src={lightbox.url}
              alt={lightbox.alt}
              className="max-h-[85vh] max-w-full rounded-sm border border-[#D4AF37]/40"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
